import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBooking } from '../../contexts/BookingContext';
import { useAuth } from '../../contexts/AuthContext';
import salonDetailsService from '../../services/salonDetailsService';
import favoritesService from '../../services/favoritesService';
import { formatPriceFCFA, formatDuration } from '../../utils/formatters';
import { getSortedHours } from '../../utils/openingHoursUtils';
import { getSalonImage, SALON_FALLBACK_IMAGE } from '../../constants/salonImages';
import './SalonDetail.css';

const SalonDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    addService,
    setSalon,
    selectedSalon,
    selectedServices,
    checkSalonChange,
    replaceServicesWithNewSalon,
  } = useBooking();

  const [salon, setSalonState] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [portfolioPreview, setPortfolioPreview] = useState(null);

  useEffect(() => {
    fetchSalonDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSalonDetails = async () => {
    try {
      setLoading(true);
      const data = await salonDetailsService.getAllSalonData(id);
      setSalonState(data.salon);
      setServices(data.services);
      setReviews(data.reviews);
      setPortfolio(data.portfolio);
      setIsFavorite(!!data.salon?.is_favorite);
    } catch (error) {
      console.error('Erreur chargement salon:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user) {
      alert('Connectez-vous pour ajouter aux favoris');
      return;
    }
    try {
      setFavLoading(true);
      const res = await favoritesService.toggleFavorite(id);
      setIsFavorite(!!res?.data?.isFavorite);
    } catch (err) {
      console.error(err);
    } finally {
      setFavLoading(false);
    }
  };

  const handleChooseService = (service) => {
    if (selectedSalon && !checkSalonChange(salon)) {
      const currentSalonName = selectedSalon.name;
      const newSalonName = salon.name;
      const nbServices = selectedServices.length;
      const message = `Vous avez ${nbServices} prestation${nbServices > 1 ? 's' : ''} du ${currentSalonName}. Changer pour le ${newSalonName} effacera votre sélection.`;
      if (!window.confirm(message)) return;
      replaceServicesWithNewSalon(salon, service);
    } else {
      if (!selectedSalon) setSalon(salon);
      addService(service);
    }
    navigate('/booking/new');
  };

  const handleBookNow = () => {
    if (!salon) return;
    setSalon(salon);
    navigate('/booking/new');
  };

  const availableCategories = salonDetailsService.getUniqueCategories(services);
  const filteredServices = salonDetailsService.filterServicesByCategory(services, activeTab);
  const sortedHours = salon ? getSortedHours(salon.opening_hours) : [];
  const avgRating = salon?.average_rating || 0;
  const reviewsCount = reviews.length;
  const minPrice = services.length
    ? Math.min(...services.map((s) => parseFloat(s.price) || 0))
    : null;

  if (loading) {
    return (
      <div className="salon-details-loading">
        <div className="spinner" />
        <p>Chargement du salon...</p>
      </div>
    );
  }

  if (!salon) {
    return (
      <div className="salon-details-error">
        <h2>Salon non trouvé</h2>
        <button type="button" onClick={() => navigate('/salons')}>
          Retour aux salons
        </button>
      </div>
    );
  }

  const heroSrc = getSalonImage(salon);
  const heroIsLogo = heroSrc === SALON_FALLBACK_IMAGE;

  return (
    <div className="salon-details-container">
      <div className="salon-hero">
        <div className="salon-hero-image">
          <img
            src={heroSrc}
            alt={salon.name}
            className={heroIsLogo ? 'salon-hero-img--logo' : undefined}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = SALON_FALLBACK_IMAGE;
              e.currentTarget.classList.add('salon-hero-img--logo');
            }}
          />
        </div>
        <div className="salon-hero-nav">
          <button type="button" className="hero-nav-btn" onClick={() => navigate(-1)} aria-label="Retour">
            ←
          </button>
          <button
            type="button"
            className={`hero-nav-btn ${isFavorite ? 'is-fav' : ''}`}
            onClick={handleToggleFavorite}
            disabled={favLoading}
            aria-label="Favori"
          >
            {isFavorite ? '♥' : '♡'}
          </button>
        </div>
        <div className="salon-hero-overlay">
          <h1 className="salon-name">{salon.name}</h1>
          <p className="salon-location">
            {salon.address}
            {salon.city ? `, ${salon.city}` : ''}
          </p>
        </div>
      </div>

      <div className="info-pills">
        {parseFloat(avgRating) > 0 && (
          <span className="info-pill">
            ★ {parseFloat(avgRating).toFixed(1)}
            <em>{reviewsCount} avis</em>
          </span>
        )}
        {salon.phone && (
          <a href={`tel:${salon.phone}`} className="info-pill info-pill--link">
            📞 {salon.phone}
          </a>
        )}
        {salon.service_type === 'both' && (
          <>
            <span className="info-pill">En salon</span>
            <span className="info-pill">À domicile</span>
          </>
        )}
        {salon.service_type === 'home' && <span className="info-pill">À domicile</span>}
        {salon.service_type === 'physical' && <span className="info-pill">En salon</span>}
        {services.length > 0 && (
          <span className="info-pill">{services.length} prestations</span>
        )}
      </div>

      <div className="main-content salon-detail-main">
        <aside className="sidebar left-sidebar">
          <div className="sidebar-section">
            <div className="reviews-summary">
              <div className="big-rating">{parseFloat(avgRating).toFixed(1)}</div>
              <div className="stars">{salonDetailsService.renderStars(avgRating)}</div>
              <p>{reviewsCount} avis clients</p>
            </div>
            <h2 className="sidebar-title">Avis récents</h2>
            {reviews.length === 0 ? (
              <p className="no-reviews">Aucun avis pour le moment</p>
            ) : (
              reviews.slice(0, 4).map((review) => (
                <div key={review.id} className="review-item">
                  <div className="review-header">
                    <div className="reviewer-name">
                      {review.client_first_name} {review.client_last_name?.charAt(0)}.
                    </div>
                    <div className="stars">{salonDetailsService.renderStars(review.rating)}</div>
                  </div>
                  <p className="review-text">{review.comment}</p>
                  <div className="review-date">
                    {salonDetailsService.formatRelativeDate(review.created_at)}
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>

        <main className="center-content">
          {salon.description && (
            <div className="section about-section">
              <h2 className="section-title">À propos</h2>
              <p className="about-text">{salon.description}</p>
            </div>
          )}

          {portfolio.length > 0 && (
            <div className="section">
              <h2 className="section-title">Portfolio</h2>
              <div className="portfolio-grid">
                {portfolio.slice(0, 6).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="portfolio-item"
                    onClick={() => setPortfolioPreview(item)}
                  >
                    <img src={item.image_url} alt={item.title || ''} />
                  </button>
                ))}
                {portfolio.length > 6 && (
                  <div className="portfolio-item portfolio-more">
                    +{portfolio.length - 6}
                    <span>Voir plus</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="section">
            <h2 className="section-title">Nos services</h2>
            <div className="services-tabs">
              <button
                type="button"
                className={`service-tab ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                Tous
              </button>
              {availableCategories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`service-tab ${activeTab === category ? 'active' : ''}`}
                  onClick={() => setActiveTab(category)}
                >
                  {salonDetailsService.capitalizeFirstLetter(category)}
                </button>
              ))}
            </div>

            <div className="services-grid">
              {filteredServices.length === 0 ? (
                <p className="no-services">Aucun service disponible</p>
              ) : (
                filteredServices.map((service) => (
                  <div key={service.id} className="service-card">
                    <div className="service-card-image">
                      {service.image ? (
                        <img src={service.image} alt={service.name} />
                      ) : (
                        <div className="service-placeholder">{service.name}</div>
                      )}
                    </div>
                    <div className="service-card-content">
                      <h3>{service.name}</h3>
                      {service.description && (
                        <p className="service-card-description">{service.description}</p>
                      )}
                      <p className="service-card-duration">{formatDuration(service.duration)}</p>
                      <div className="service-card-footer">
                        <div className="service-card-price">{formatPriceFCFA(service.price)}</div>
                        <button
                          type="button"
                          className="quick-book-btn"
                          onClick={() => handleChooseService(service)}
                        >
                          Choisir
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </main>

        <aside className="sidebar right-sidebar">
          <div className="sidebar-section">
            <h2 className="sidebar-title">Contact</h2>
            {(salon.address || salon.city) && (
              <p className="salon-contact-row">
                <span className="salon-contact-label">Adresse</span>
                <span>{[salon.address, salon.city].filter(Boolean).join(', ')}</span>
              </p>
            )}
            {salon.phone ? (
              <p className="salon-contact-row">
                <span className="salon-contact-label">Téléphone</span>
                <a href={`tel:${salon.phone}`} className="salon-contact-phone">
                  {salon.phone}
                </a>
              </p>
            ) : (
              <p className="salon-contact-row salon-contact-muted">
                Numéro non renseigné
              </p>
            )}
          </div>
          <div className="sidebar-section">
            <h2 className="sidebar-title">Horaires</h2>
            {sortedHours.length === 0 ? (
              <p>Horaires non disponibles</p>
            ) : (
              sortedHours.map(({ day, label, hours }) => (
                <div key={day} className="hour-row">
                  <span className="day">{label}</span>
                  {hours.closed ? (
                    <span className="time closed">Fermé</span>
                  ) : (
                    <span className="time">
                      {hours.open} - {hours.close}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </aside>
      </div>

      <div className="salon-sticky-footer">
        <div className="salon-sticky-inner">
          <div className="salon-sticky-price">
            {minPrice != null && minPrice > 0 ? (
              <>
                <span className="sticky-from">À partir de</span>
                <strong>{formatPriceFCFA(minPrice)}</strong>
              </>
            ) : (
              <strong>Réserver</strong>
            )}
          </div>
          <button type="button" className="salon-sticky-cta" onClick={handleBookNow}>
            Réserver
          </button>
        </div>
      </div>

      {portfolioPreview && (
        <div
          className="portfolio-modal"
          onClick={() => setPortfolioPreview(null)}
          role="presentation"
        >
          <img
            src={portfolioPreview.image_url}
            alt={portfolioPreview.title || ''}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="portfolio-modal-close"
            onClick={() => setPortfolioPreview(null)}
          >
            Fermer
          </button>
        </div>
      )}
    </div>
  );
};

export default SalonDetail;
